/**
 * The paperdoll — what you are wearing, and what it is worth.
 *
 * Phase 2c rebuilt this. There are no weapon rails any more (mods went with the guns), and the
 * doll grew from five armour squares to seven slots: six armour and the stick in your hand. The
 * body sits in the middle with head / shoulders / chest down its left and hands / legs / feet down
 * its right — the arrangement every tab-target game has used since it was invented, because a
 * player reads it without being told what it is.
 *
 * **It draws from `ItemStack[]`, not from `/api/equipment`.** Durability lives on the stack and
 * nowhere else, so the doll reads the same list the Stash grid does (`/api/stash` returns the
 * equipment locations too — a worn piece is an ordinary item row) and runs it through the shared
 * `equippedStacks` / `equippedArmor` / `equippedFor`. That means the armour number on screen is
 * computed by the same function the server calls when the hit lands, broken pieces excluded.
 *
 * Writes are still server-authoritative and still one round trip: `PUT /api/equipment` decides
 * whether a piece exists, is yours, fits the slot, and is legal for your Calling *and* level, and
 * `onChanged` re-reads afterwards because equipping displaces the old piece into the backpack.
 */

import type { VNode } from 'preact';
import { useState } from 'preact/hooks';
import {
  ARMOR_MAX_FLAT,
  ARMOR_MAX_FRACTION,
  ARMOR_SLOTS,
  ARMOR_SLOT_NAMES,
  ITEMS,
  MAX_DURABILITY,
  UNARMED,
  callingCanUse,
  durabilityOf,
  dps,
  equipLocationFor,
  equippedArmor,
  equippedStacks,
  isBroken,
  isEquipLocation,
  levelCanUse,
  secondaryLabel,
  weaponForItem,
  type ArmorSlot,
  type ItemDef,
  type ItemStack,
} from '@stillsun/shared';
import { putEquipment, type EquipSlot } from '../../net/progress-api.js';
import { errText, itemStatLines, rarityHex, rarityRailHex } from './common.js';
import { ARMOR_SLOT_ICON, Icon, ItemIcon } from '../icons.js';

/** `ApiError.message` carries the server's `error` code verbatim; the player is owed English. */
const EQUIP_ERRORS: Record<string, string> = {
  not_found: 'That piece is not in your stash any more.',
  not_armor: 'That is not something you can wear.',
  not_a_weapon: 'That is not something you can swing.',
  wrong_slot: 'That does not go there.',
  wrong_calling: 'That is not for your Calling.',
  level_too_low: 'You have not grown into that yet.',
  no_room: 'No room to take it off — clear a backpack or stash slot first.',
  'invalid body': 'The quartermaster will not take that slot — this build is ahead of the server.',
};

const equipError = (err: unknown): string =>
  (err instanceof Error ? EQUIP_ERRORS[err.message] : undefined) ??
  errText(err, 'The quartermaster is not answering.');

/**
 * The doll's seven squares, in the order they are laid out rather than in `ARMOR_SLOTS` order:
 * down the left, down the right, then the weapon under the body. The CSS grid names the same
 * areas, so moving a square is one line in each file and never a re-order here.
 */
const DOLL_SLOTS: readonly { slot: EquipSlot; area: string; label: string }[] = [
  { slot: 'head', area: 'head', label: ARMOR_SLOT_NAMES.head },
  { slot: 'shoulders', area: 'shoulders', label: ARMOR_SLOT_NAMES.shoulders },
  { slot: 'chest', area: 'chest', label: ARMOR_SLOT_NAMES.chest },
  { slot: 'hands', area: 'hands', label: ARMOR_SLOT_NAMES.hands },
  { slot: 'legs', area: 'legs', label: ARMOR_SLOT_NAMES.legs },
  { slot: 'feet', area: 'feet', label: ARMOR_SLOT_NAMES.feet },
  { slot: 'weapon', area: 'weapon', label: 'Weapon' },
];

const isArmorSlot = (slot: EquipSlot): slot is ArmorSlot => slot !== 'weapon';

/** The secondaries, in the words the tooltip uses, so the doll and the card never disagree. */
const SECONDARY_LABEL: Record<string, string> = {
  maxHp: 'Max HP',
  maxStamina: 'Max stamina',
  damageMult: 'Damage',
  speedMult: 'Move speed',
  staggerResist: 'Stagger resist',
  cooldownMult: 'Cooldowns',
};
const SECONDARY_PCT = new Set(['damageMult', 'speedMult', 'staggerResist', 'cooldownMult']);

function secondaryText(key: string, value: number): string {
  if (SECONDARY_PCT.has(key)) {
    return `${value > 0 ? '+' : '−'}${String(Math.abs(Math.round(value * 1000) / 10))}%`;
  }
  return `${value > 0 ? '+' : '−'}${String(Math.abs(Math.round(value)))}`;
}

export interface LoadoutProps {
  /** Everything the player owns — worn rows included. `/api/stash` returns all of it. */
  items: ItemStack[];
  token: string;
  /**
   * `CallingId` (0 = none picked yet), or **null when the ledger has not answered** — an offline
   * guest, or a client that dropped straight into a raid. Null means "do not pre-refuse anything
   * locally": the server is the authority on both rules and will say no honestly, and greying a
   * legal piece on a missing read reads as having lost the right to wear your own armour.
   */
  calling: number | null;
  /** Character level, or null when unknown — see `calling`. */
  level: number | null;
  /** Re-read the stash: every accepted equip moves item rows server-side. */
  onChanged: () => void;
  /**
   * Look, do not touch. Set in the world (`ui/overlay.tsx`): a raid room holds its own copy of
   * your items, and `PUT /api/equipment` writes the persistent table underneath it, so a helm
   * swapped mid-raid would be silently undone at extract. Hover and right-click still work.
   */
  readOnly?: boolean;
  /** Right-click on a worn piece. The host owns the menu, so only one is ever open. */
  onContext?: (stack: ItemStack, x: number, y: number) => void;
  /** Hover. Same reason: the host owns the one tooltip. */
  onHover?: (stack: ItemStack | null, x: number, y: number) => void;
}

export function Loadout(props: LoadoutProps): VNode {
  const { calling, items, level, token } = props;
  const readOnly = props.readOnly === true;
  const [note, setNote] = useState('');
  const [picking, setPicking] = useState<EquipSlot | null>(null);
  const [busy, setBusy] = useState(false);

  const { weapon, bySlot } = equippedStacks(items);
  const stackIn = (slot: EquipSlot): ItemStack | null =>
    slot === 'weapon' ? weapon : (bySlot[slot] ?? null);

  const commit = (slot: EquipSlot, uid: number | null): void => {
    if (busy) return;
    if (readOnly) {
      setNote('Not out here — what you are wearing is set before you deploy.');
      return;
    }
    setPicking(null);
    setNote('');
    setBusy(true);
    void putEquipment(token, slot, uid).then(
      () => {
        setBusy(false);
        // Equipping displaces the old piece into the backpack or the stash; the grid is stale.
        props.onChanged();
      },
      (err: unknown) => {
        setBusy(false);
        setNote(equipError(err));
      },
    );
  };

  /** Refuse locally what the server would refuse anyway, so the reason arrives instantly. */
  const tryEquip = (slot: EquipSlot, stack: ItemStack): void => {
    const def = ITEMS[stack.itemId];
    if (!def) {
      setNote('That is not something this build knows about.');
      return;
    }
    const wanted = equipLocationFor(def);
    if (wanted === null) {
      setNote(`${def.name} is not something you can wear or wield.`);
      return;
    }
    if (slot === 'weapon' ? def.kind !== 'weapon' : def.armorSlot !== slot) {
      const where = slot === 'weapon' ? 'in your hand' : `on your ${slotWord(slot)}`;
      setNote(`${def.name} does not go ${where}.`);
      return;
    }
    // Both checks are skipped when the ledger is unread: the server decides either way, and a
    // refusal invented from a missing number is worse than a round trip that says no.
    if (calling !== null && !callingCanUse(def, calling)) {
      setNote(`${def.name} is not for your Calling.`);
      return;
    }
    if (level !== null && !levelCanUse(def, level)) {
      setNote(`${def.name} needs level ${String(def.levelReq ?? 1)}.`);
      return;
    }
    commit(slot, stack.uid);
  };

  const onDrop = (slot: EquipSlot, raw: string): void => {
    if (!/^\d+$/.test(raw)) return;
    const stack = items.find((s) => s.uid === Number(raw));
    if (!stack) return;
    tryEquip(slot, stack);
  };

  /** Owned, legal-to-*hold*, and not already worn. Illegal pieces are shown greyed, not hidden. */
  const candidates = (slot: EquipSlot): ItemStack[] =>
    items.filter((s) => {
      if (isEquipLocation(s.location)) return false;
      const def = ITEMS[s.itemId];
      if (!def) return false;
      return slot === 'weapon' ? def.kind === 'weapon' : def.armorSlot === slot;
    });

  const armour = equippedArmor(items);
  const worn = weapon === null || isBroken(weapon) ? null : weaponForItem(weapon.itemId);
  const held = worn ?? UNARMED;
  const secondaries = Object.entries(armour.mods)
    .filter((entry): entry is [string, number] => typeof entry[1] === 'number' && entry[1] !== 0)
    .sort((a, b) => (SECONDARY_LABEL[a[0]] ?? a[0]).localeCompare(SECONDARY_LABEL[b[0]] ?? b[0]));
  const brokenCount = [...ARMOR_SLOTS.map((s) => bySlot[s]), weapon].filter(
    (s): s is ItemStack => s !== null && s !== undefined && isBroken(s),
  ).length;

  return (
    <div class="hub-loadout">
      {note.length > 0 ? <p class="hub-error">{note}</p> : null}
      {brokenCount > 0 ? (
        <p class="hub-error">
          {brokenCount === 1
            ? 'One piece is broken and giving you nothing. Sabel will mend it.'
            : `${String(brokenCount)} pieces are broken and giving you nothing. Sabel will mend them.`}
        </p>
      ) : null}

      <div class="hub-doll-wrap">
        <div class={`hub-doll${busy ? ' busy' : ''}`}>
          {DOLL_SLOTS.map((cell) => {
            const stack = stackIn(cell.slot);
            const def = stack === null ? undefined : ITEMS[stack.itemId];
            const dur = stack === null ? MAX_DURABILITY : durabilityOf(stack);
            const broken = stack !== null && isBroken(stack);
            return (
              <div
                key={cell.slot}
                class={`hub-doll-slot${def ? ' filled' : ''}${broken ? ' broken' : ''}${
                  picking === cell.slot ? ' picking' : ''
                }`}
                // The rarity rail is an inline colour, so it would beat `.broken`'s border in
                // the cascade — a ruined Epic would keep its purple edge. Broken wins here
                // instead: it is the more urgent of the two things the border can say.
                style={{
                  gridArea: cell.area,
                  ...(broken
                    ? { borderColor: 'var(--danger)' }
                    : def
                      ? { borderColor: rarityRailHex(def.rarity) }
                      : {}),
                }}
                role="button"
                tabIndex={0}
                aria-label={`${cell.label} — ${def?.name ?? 'empty'}`}
                // An empty slot names the secondary it *would* carry, so a player can see what
                // they are missing rather than only that something is missing.
                title={
                  def === undefined
                    ? isArmorSlot(cell.slot)
                      ? `${cell.label} — empty · carries ${secondaryLabel(cell.slot)}`
                      : 'Nothing in hand — you are swinging your fists'
                    : undefined
                }
                onClick={() => {
                  if (readOnly) return;
                  if (stack !== null) commit(cell.slot, null);
                  else setPicking(picking === cell.slot ? null : cell.slot);
                }}
                onKeyDown={(e) => {
                  if (readOnly || (e.key !== 'Enter' && e.key !== ' ')) return;
                  e.preventDefault();
                  if (stack !== null) commit(cell.slot, null);
                  else setPicking(picking === cell.slot ? null : cell.slot);
                }}
                onContextMenu={(e) => {
                  e.preventDefault();
                  if (stack !== null) props.onContext?.(stack, e.clientX, e.clientY);
                }}
                // Anchored on entry, not followed on mousemove: the same doll is drawn inside the
                // in-raid overlay, whose whole tree re-renders on every hover update and whose
                // frame is CPU-bound. A card that holds still over a fixed square reads fine.
                onMouseEnter={(e) => {
                  if (stack !== null) props.onHover?.(stack, e.clientX, e.clientY);
                }}
                onMouseLeave={() => {
                  props.onHover?.(null, 0, 0);
                }}
                onDragOver={(e) => {
                  if (readOnly) return;
                  e.preventDefault();
                  if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
                }}
                onDrop={(e) => {
                  if (readOnly) return;
                  e.preventDefault();
                  onDrop(cell.slot, e.dataTransfer?.getData('text/plain') ?? '');
                }}
              >
                {def ? (
                  <ItemIcon def={def} size={34} durability={dur} />
                ) : (
                  <span class="hub-doll-bare">
                    <Icon
                      name={
                        cell.slot === 'weapon' ? 'weapon' : (ARMOR_SLOT_ICON[cell.slot] ?? 'pip')
                      }
                      size={26}
                    />
                  </span>
                )}
                <span class="hub-doll-label">{cell.label}</span>
              </div>
            );
          })}

          {/* The figure. Not a render — see the note in the report — but a body-shaped anchor for
              seven squares, tinted by the best thing on it so a full set reads at a glance. */}
          <div class="hub-doll-figure" style={{ gridArea: 'figure' }} aria-hidden="true">
            <Icon name="stand" size={92} />
          </div>
        </div>

        <div class="hub-doll-stats">
          <h4 class="hub-cap">What it is worth</h4>
          <div class="hub-stat">
            <span class="l">Armour</span>
            <span class="v">{`+${String(armour.armor)}`}</span>
            <span class="was">{`caps at ${String(ARMOR_MAX_FLAT)} or ${String(Math.round(ARMOR_MAX_FRACTION * 100))}% of a hit`}</span>
          </div>
          {secondaries.length === 0 ? (
            <div class="hub-stat">
              <span class="l">Secondaries</span>
              <span class="v">—</span>
              <span class="was">Common pieces carry armour and nothing else</span>
            </div>
          ) : (
            secondaries.map(([key, value]) => (
              <div key={key} class="hub-stat">
                <span class="l">{SECONDARY_LABEL[key] ?? key}</span>
                <span class="v">{secondaryText(key, value)}</span>
              </div>
            ))
          )}

          <h4 class="hub-cap">In hand</h4>
          <div class="hub-stat">
            <span class="l">{held.name}</span>
            <span class="v">{`${String(held.damage[0])}–${String(held.damage[1])}`}</span>
            <span class="was">
              {weapon !== null && worn === null
                ? 'broken — you are swinging your fists'
                : `${(held.speedMs / 1000).toFixed(1)} s swing · ${String(held.range)} m reach`}
            </span>
          </div>
          <div class="hub-stat">
            <span class="l">Damage per second</span>
            <span class="v">{dps(held).toFixed(1)}</span>
          </div>
        </div>
      </div>

      {readOnly ? (
        <p class="hub-hint hub-doll-locked">
          What you carry is what you deployed with. Change it in Lanternfall.
        </p>
      ) : null}

      {picking !== null && !readOnly ? (
        <Picker
          slot={picking}
          label={DOLL_SLOTS.find((c) => c.slot === picking)?.label ?? 'Slot'}
          stacks={candidates(picking)}
          calling={calling}
          level={level}
          busy={busy}
          onPick={(stack) => {
            tryEquip(picking, stack);
          }}
          onCancel={() => {
            setPicking(null);
          }}
        />
      ) : null}
    </div>
  );
}

const slotWord = (slot: EquipSlot): string =>
  isArmorSlot(slot) ? ARMOR_SLOT_NAMES[slot].toLowerCase() : 'hand';

// ------------------------------------------------------------------- the picker

/**
 * The click path, for anyone not dragging: every piece owned for this slot, each one saying what
 * it would give and — when it is out of reach — why. A piece is never hidden for being illegal;
 * "Vanguard only" on a chip you cannot press is the whole reason the Vanguard line exists.
 */
function Picker(props: {
  slot: EquipSlot;
  label: string;
  stacks: ItemStack[];
  /** Null when the ledger has not answered; nothing is pre-refused in that case. */
  calling: number | null;
  level: number | null;
  busy: boolean;
  onPick: (stack: ItemStack) => void;
  onCancel: () => void;
}): VNode {
  const reasonFor = (def: ItemDef): string | null => {
    if (props.calling !== null && !callingCanUse(def, props.calling)) return 'not for your Calling';
    if (props.level !== null && !levelCanUse(def, props.level)) {
      return `needs level ${String(def.levelReq ?? 1)}`;
    }
    return null;
  };
  return (
    <div class="hub-picker">
      <span class="hub-label">
        <Icon
          name={props.slot === 'weapon' ? 'weapon' : (ARMOR_SLOT_ICON[props.slot] ?? 'pip')}
          size={13}
        />
        {props.slot === 'weapon' ? ' Take in hand' : ` Wear on your ${slotWord(props.slot)}`}
      </span>
      {props.stacks.length === 0 ? (
        <span class="hub-hint">
          {props.slot === 'weapon'
            ? 'Nothing to swing but your fists. The Compact sells a starter stick.'
            : `Nothing for your ${slotWord(props.slot)} yet — the good lines are Calling-locked and start at level 3.`}
        </span>
      ) : (
        <div class="hub-chips">
          {props.stacks.map((stack) => {
            const def = ITEMS[stack.itemId];
            if (!def) return null;
            const reason = reasonFor(def);
            const dur = durabilityOf(stack);
            const stats = itemStatLines(def);
            return (
              <button
                key={stack.uid}
                type="button"
                class={`hub-chip hub-chip-item${reason === null ? '' : ' off'}`}
                style={{ borderColor: rarityRailHex(def.rarity) }}
                disabled={props.busy || reason !== null}
                title={reason ?? stats.join(' · ')}
                onClick={() => {
                  props.onPick(stack);
                }}
              >
                <ItemIcon def={def} size={22} durability={dur} />
                <span class="hub-chip-body">
                  <span class="hub-chip-name" style={{ color: rarityHex(def.rarity) }}>
                    {def.name}
                  </span>
                  <span class="hub-chip-sub">
                    {reason ??
                      (stats[0] ?? '') +
                        (dur < MAX_DURABILITY ? ` · ${String(dur)}/${String(MAX_DURABILITY)}` : '')}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      )}
      <button type="button" class="hub-chip" onClick={props.onCancel}>
        Cancel
      </button>
    </div>
  );
}
